import React from 'react';
import T from 'Components/Helpers/translate';
import type { ProspectingCoveragePanel } from './types';

/**
 * What the assistant could not answer, and why, before anything is done about it.
 *
 * Structured search covers 13 markets and the filters in the registry: instant
 * and free. Everything else needs web research, which is slower and spends
 * credits. This card exists so neither half is decided silently - dropping the
 * uncovered part reads to a rep as "there are none", and spending credits without
 * being asked is worse.
 *
 * There is no confirm button. Running the web research needs an ICPHypothesis,
 * which today only the seller-context strategies produce - there is no path from
 * a rep's filters to one. A button that cannot do anything is worse than an
 * honest explanation, so the card offers the covered alternative instead.
 *
 * The amber card of the workspace design (WEB-10886).
 */
const REASON_KEY: Record<string, string> = {
	country: 'prospecting.v2.coverage.reasonCountry',
	noSuchFilter: 'prospecting.v2.coverage.reasonNoFilter'
};

const CoverageCard = ({ panel, onAsk }: { panel: ProspectingCoveragePanel; onAsk?: (text: string) => void }) => {
	if (!panel.outOfCoverage?.length) return null;

	return (
		/* Hex rather than the workspace's tokens: the AI Workspace shows a
		   prospecting conversation too, and has no `--pw-*` variables. 8px
		   literally — `rounded-lg` is `--radius` (10px), not the design's card. */
		<div className="rounded-[8px] border border-[#fad668] bg-alert-light px-3 py-2.5">
			<div className="text-[12px] font-semibold text-alert-dark">{T('prospecting.v2.coverage.title')}</div>

			<ul className="m-0 mt-[3px] flex list-none flex-col gap-2 p-0">
				{panel.outOfCoverage.map(item => (
					<li key={item.ask} className="text-[11px] leading-[1.5] text-grey-11">
						<span className="block">{item.ask}</span>
						<span className="block text-grey-10">
							{T(REASON_KEY[item.reason] ?? 'prospecting.v2.coverage.reasonNoFilter')}
						</span>
					</li>
				))}
			</ul>

			<div className="mt-2 text-[11px] leading-[1.5] text-grey-10">
				{T('prospecting.v2.coverage.covered', {
					markets: panel.covered?.countries?.length ?? 0,
					filters: panel.covered?.filterCount ?? 0
				})}
			</div>
			{panel.instantAlternative && onAsk ? (
				<button
					type="button"
					onClick={() => onAsk(panel.instantAlternative as string)}
					className="mt-1.5 rounded-full border border-grey-5 bg-background px-3 py-1 text-[11px] text-grey-11 hover:border-grey-7 hover:text-foreground"
				>
					{panel.instantAlternative}
				</button>
			) : null}
		</div>
	);
};

export default CoverageCard;
